// audioFloor — single-source coordinator for the iOS audio session
// when narration and the speech recognizer compete for it.
//
// THE PROBLEM
//
// iOS gives the app one audio session. SFSpeechRecognizer (the
// Capacitor speech-recognition plugin) takes it in .playAndRecord
// mode while listening; HTML5 <audio> and Web Audio takes it during
// playback. They cannot share — when SR claims while audio is loading
// the audio.play() either silently fails or gets clipped halfway.
//
// THE PROTOCOL
//
// One narrator at a time holds "the floor." Any path that's about to
// play narration audio (step lines, voicelines, per-step TTS) claims
// the floor synchronously BEFORE the fetch/decode/play begins. The
// recognizer listens on `chadwick:speak-start` to suspend itself, and
// on `chadwick:speak-end` to come back. The event names are legacy —
// Chadwick is one narrator among many (cuisine-accented voices for
// international courses, future hosts). Treat them as the generic
// "narrator floor change" channel; the rename can come later.
//
// claim(ttlMs)  — refcount++; on the first claim, dispatch speak-start.
//                 Schedules a hard release after ttlMs so a failed
//                 fetch / decode error / orphaned <audio> can't deadlock
//                 the recognizer offline forever. ttlMs should be
//                 generous (the recognizer suspending is cheap; the
//                 narration silently dying is expensive).
// extend(ttlMs) — push the safety release further out. Real audio
//                 onPlay handlers should call this with a duration that
//                 covers the rest of the clip, so a slow first-load
//                 doesn't trigger the safety release mid-playback.
// release()     — refcount--; when the last claim drops, dispatch
//                 speak-end immediately and clear the safety timer.
//
// Refcount semantics matter: a step-change effect can claim the floor
// for a TTS player at the same moment a voiceline is firing. Both
// finish; the second release dispatches speak-end. A simple boolean
// would have either prematurely un-suspended the recognizer mid-clip
// or held it offline long after the second clip ended.

let claims = 0;
let safetyTimer = null;

function log(kind, ttlMs) {
  // eslint-disable-next-line no-console
  console.log(`[floor] ${kind} claims=${claims}${ttlMs != null ? ` ttl=${ttlMs}ms` : ""}`);
}

function dispatch(eventName) {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(eventName));
  } catch { /* ignore (older Safari) */ }
}

function clearSafety() {
  if (safetyTimer) {
    clearTimeout(safetyTimer);
    safetyTimer = null;
  }
}

function armSafety(ttlMs) {
  clearSafety();
  if (!Number.isFinite(ttlMs) || ttlMs <= 0) return;
  safetyTimer = setTimeout(() => {
    safetyTimer = null;
    if (claims > 0) {
      // Forced release — something claimed but never released.
      // Better to un-suspend the recognizer than leave it offline.
      log("SAFETY-EXPIRE force-release", null);
      claims = 0;
      dispatch("chadwick:speak-end");
    }
  }, ttlMs);
}

export function claim(ttlMs = 8000) {
  claims += 1;
  log("claim", ttlMs);
  if (claims === 1) {
    dispatch("chadwick:speak-start");
  }
  armSafety(ttlMs);
}

export function extend(ttlMs) {
  if (claims === 0) return;
  log("extend", ttlMs);
  armSafety(ttlMs);
}

export function release() {
  if (claims === 0) return;
  claims -= 1;
  log("release", null);
  if (claims === 0) {
    clearSafety();
    dispatch("chadwick:speak-end");
  }
}

// Force-clear all claims and dispatch end. Used when CookMode unmounts
// or the cook session ends — anything still holding the floor at that
// point is a leak we want to drop.
export function forceRelease() {
  if (claims === 0 && !safetyTimer) return;
  claims = 0;
  clearSafety();
  dispatch("chadwick:speak-end");
}

// Read-only snapshot for debug / tests.
export function _state() {
  return { claims, hasSafety: !!safetyTimer };
}

// True iff at least one narrator currently holds the floor. Used by
// the speech recognizer's post-fire restart logic to decide whether
// it's safe to come back online or whether narration is still mid-
// playback.
export function isBusy() {
  return claims > 0;
}
